<script setup lang="ts">
/**
 * One command on a decision card, as the portal and zerohuman.com draw it: `approve` is the dirty-white block that
 * goes ahead (Approve, Send answers, a chosen Reply), `reject` the quiet outline that declines or sends work back
 * (Reject, Request changes, Leave it). The card's edge carries the action cyan, so the commands stay off it.
 *
 * Styled here rather than through the Tailwind kit, as ZhButton is, so the site draws the same command without
 * generating the kit's utilities. `drawn` renders a span, a picture of the command for the site's figures.
 */
withDefaults(
    defineProps<{
        tone?: 'approve' | 'reject'
        drawn?: boolean
        disabled?: boolean
        type?: 'button' | 'submit'
    }>(),
    { tone: 'approve', drawn: false, disabled: false, type: 'button' },
)
</script>

<template>
    <span v-if="drawn" class="zh-decision-button" :data-tone="tone"><slot /></span>
    <button v-else class="zh-decision-button" :data-tone="tone" :type="type" :disabled="disabled">
        <slot />
    </button>
</template>

<style scoped>
/* The control voice (BG §19.3): mono, uppercase, system tracking. */
.zh-decision-button {
    display: inline-flex;
    align-items: center;
    min-height: 2.25rem;
    padding-inline: 0.875rem;
    border: 1px solid transparent;
    border-radius: 0;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    white-space: nowrap;
    transition:
        transform var(--motion-ui) ease-out,
        filter var(--motion-ui) ease-out,
        border-color var(--motion-ui) ease-out,
        color var(--motion-ui) ease-out;
}

.zh-decision-button[data-tone="approve"] {
    border-color: var(--dirty-white);
    background: var(--dirty-white);
    color: var(--void-black);
}

.zh-decision-button[data-tone="reject"] {
    border-color: var(--line);
    background: transparent;
    color: var(--ink-2);
}

button.zh-decision-button {
    cursor: pointer;
}

/* BG §22.4's hover: brighter, and a 1px shift that reads as a glitch rather than a lift. */
button.zh-decision-button[data-tone="approve"]:hover:not(:disabled) {
    filter: brightness(1.08);
    transform: translateX(1px);
}

button.zh-decision-button[data-tone="reject"]:hover:not(:disabled) {
    border-color: var(--ink-1);
    color: var(--ink-1);
    transform: translateX(1px);
}

button.zh-decision-button:active:not(:disabled) {
    transform: translateX(1px) scaleY(0.97);
}

button.zh-decision-button:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}

button.zh-decision-button:disabled {
    cursor: not-allowed;
    filter: saturate(0.5) brightness(0.8);
}

@media (prefers-reduced-motion: reduce) {
    .zh-decision-button {
        transition: none;
    }

    button.zh-decision-button:hover:not(:disabled),
    button.zh-decision-button:active:not(:disabled) {
        transform: none;
    }
}
</style>
